import { useState, useEffect, useMemo, useContext, useRef } from "react";
import { useParams } from "react-router-dom";
import FormEditor from "../components/Form/Editor/FormEditor";
import SideMenu from "../components/UI/SideMenu";
import TrackingHeader from "../components/UI/TrackingHeader";
import { ASSESSMENT_ID, DELIVERY_ID } from "../constants/formTypes";
import Client from "../models/serviceModels/Client";
import Form from "../models/serviceModels/Form";
import Program from "../models/serviceModels/Program";
import { getTrackingOptions } from "../utils/sideMenuHelpers";
import EditableFormField from '../models/pageModels/EditableFormField';
import { createFormInstance, deleteFormInstance, getForm, getLatestFormInstances } from "../services/formsService";
import Loader from "../components/UI/Loader";
import Button from "../components/UI/Button";
import LatestFormInstance from "../models/serviceModels/LatestFormInstance";
import { formatDistance, parseISO } from "date-fns";
import ErrorMessages from "../components/UI/ErrorMessages";
import { mapFormFieldToEditableFormField, normalizeFormInstanceFieldValue } from "../mappers/formFieldMapper";
import ApiErrorMessages from "../components/UI/ApiErrorMessages";
import { UPLOADED } from "../constants/fileUploadStatus";
import { MeContext, OrganizationContext } from "../App";
import { ADMIN_ROLE_ID, CONTRIBUTOR_ROLE_ID } from "../constants/userRoles";
import Dropdown from "../components/UI/Dropdown";
import Modal from "../components/UI/Modal";
import PreviewFormInstance from "../components/Form/PreviewFormInstance";
import getclassNames from "../utils/getClassNames";
import Input from "../components/UI/Input";
import HouseholdTrackingControls from "../components/Tracking/HouseholdTrackingControls";
import HouseholdTrackingProgress from "../components/Tracking/HouseholdTrackingProgress";
import HouseholdTrackingSummary from "../components/Tracking/HouseholdTrackingSummary";
import { bulkHouseholdTrackingEnabled } from "../utils/features";
import {
    buildHouseholdTrackingGroups,
    buildHouseholdTrackingTargets,
    getSelectableHouseholdTrackingClientIds,
    getHouseholdTrackingIneligiblePeopleCount,
    getHouseholdTrackingReviewedPeopleCount,
    resolveHouseholdTrackingEligibility,
    runHouseholdTrackingSubmissions
} from "../utils/householdTracking";
import {
    HouseholdTrackingEligibility,
    HouseholdTrackingProgress as HouseholdTrackingProgressModel,
    HouseholdTrackingSummary as HouseholdTrackingSummaryModel
} from "../models/pageModels/HouseholdTracking";
import { getClientById } from "../services/clientsService";
import { getEnrolledPrograms } from "../services/programsService";
import toast from "react-hot-toast";

export default function Tracking() {
    const { type = '' } = useParams();
    const [selectedClient, setSelectedClient] = useState<Client>();
    const [selectedProgram, setSelectedProgram] = useState<Program>();
    const [errors, setErrors] = useState<string[]>([]);
    const [submitError, setSubmitError] = useState<any>();
    const [selectedForm, setSelectedForm] = useState<Form>();
    const [fields, setFields] = useState<EditableFormField[]>([]);
    const [instances, setInstances] = useState<LatestFormInstance[]>();
    const [loadingFields, setLoadingFields] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [loadingInstances, setLoadingInstances] = useState(false);
    const organizationContext = useContext(OrganizationContext);
    const [confirmInstanceDeletion, setConfirmInstanceDeletion] = useState<LatestFormInstance>();
    const [modalErrors, setModalErrors] = useState<any>();
    const [highlightInstance, setHighlightInstance] = useState<LatestFormInstance>();
    const [previewInstance, setPreviewInstance] = useState<LatestFormInstance>();
    const [deleting, setDeleting] = useState(false);
    const [loadingSelectedClient, setLoadingSelectedClient] = useState(false);
    const [submitForHousehold, setSubmitForHousehold] = useState(false);
    const [selectedHouseholdClientIds, setSelectedHouseholdClientIds] = useState<string[]>([]);
    const [householdEligibility, setHouseholdEligibility] = useState<Record<string, HouseholdTrackingEligibility>>({});
    const [loadingHouseholdEligibility, setLoadingHouseholdEligibility] = useState(false);
    const [householdProgress, setHouseholdProgress] = useState<HouseholdTrackingProgressModel>();
    const [householdSummary, setHouseholdSummary] = useState<HouseholdTrackingSummaryModel>();
    const householdEligibilityRequest = useRef(0);
    const me = useContext(MeContext);
    const householdTrackingGroups = useMemo(
        () => selectedClient ? buildHouseholdTrackingGroups(selectedClient) : [],
        [selectedClient]
    );

    useEffect(() => {
        document.title = "Track Data"
    }, []);

    const typeLabel = useMemo(() => {
        const lowerType = type.toLowerCase();
        return lowerType === 'delivery' ? 'Outputs and Outcomes' :
            lowerType === 'assessments' ? 'Assessments' :
                lowerType === 'plan' ? 'Plans' :
                    '';
    }, [type]);

    const typeId = useMemo(() => {
        const lowerType = type.toLowerCase();
        return lowerType === 'delivery' ? DELIVERY_ID :
            lowerType === 'assessments' ? ASSESSMENT_ID :
                0;
    }, [type]);
    
    useEffect(() => {
        loadInstances(typeId);
    }, [typeId]);

    useEffect(() => {
        if (!highlightInstance) {
            return;
        }

        const timeoutReference = setTimeout(() => {
            setHighlightInstance(undefined);
        }, 3000);

        return  () => {
            if (timeoutReference){
                clearTimeout(timeoutReference);
            }
        }
    }, [highlightInstance]);

    useEffect(() => {
        if (selectedForm && selectedForm.id) {
            setErrors([]);
            setLoadingFields(true);

            getForm(selectedForm.id, selectedForm.versionId).then(f => {
                setFields([
                    ...f.fields.map(fe => mapFormFieldToEditableFormField(fe))
                ]);
            }).catch(e => {
                setErrors(['Failed to get fields']);
                console.error(e);
            }).finally(() => {
                setLoadingFields(false);
            });
        } else {
            setFields([]);
        }
    }, [selectedForm, typeId]);
    
    function resetHouseholdTracking() {
        householdEligibilityRequest.current += 1;
        setSubmitForHousehold(false);
        setSelectedHouseholdClientIds([]);
        setHouseholdEligibility({});
        setLoadingHouseholdEligibility(false);
        setHouseholdProgress(undefined);
        setHouseholdSummary(undefined);
    }

    const handleClientSelected = async (client?: Client) => {
        resetHouseholdTracking();
        setSubmitError(undefined);

        if (!client) {
            setSelectedClient(undefined);
            return;
        }

        if (!bulkHouseholdTrackingEnabled) {
            setSelectedClient(client);
            return;
        }

        setSelectedClient(client);
        setLoadingSelectedClient(true);
        try {
            setSelectedClient(await getClientById({ id: client.id }));
        } catch (error) {
            console.error(error);
            setSelectedClient(undefined);
            setErrors(['Failed to load the selected participant']);
        } finally {
            setLoadingSelectedClient(false);
        }
    };

    const handleProgramSelected = (program?: Program) => {
        resetHouseholdTracking();
        setSelectedProgram(program);
    };

    const handleFormSelected = (form?: Form) => {
        resetHouseholdTracking();
        setSelectedForm(form);
    };

    const getHeaderContent = () => {
        return <TrackingHeader client={selectedClient}
            program={selectedProgram}
            form={selectedForm}
            title={`Track ${typeLabel}`}
            onClientSelected={handleClientSelected}
            onProgramSelected={handleProgramSelected}
            onFormSelected={handleFormSelected}
            disabled={submitting || loadingSelectedClient}
            type={type} />;
    };

    const loadInstances = (formTypeId: number) => {
        setLoadingInstances(true);

        getLatestFormInstances(formTypeId).then(i => {
            setInstances([
                ...i
            ])
        }).catch((e) => {
            console.error(e);
            setErrors(['Failed to load previous submissions']);
        }).finally(() => {
            setLoadingInstances(false);
        })
    };

    const handleOnFieldsChange = (fields: EditableFormField[]) => {
        setFields([
            ...fields
        ]);
    };

    const handleSubmitForHouseholdChange = async (checked: boolean) => {
        householdEligibilityRequest.current += 1;
        const requestId = householdEligibilityRequest.current;
        setSubmitForHousehold(checked);
        setHouseholdSummary(undefined);
        setHouseholdProgress(undefined);

        if (!checked || !selectedProgram || !selectedClient) {
            setHouseholdEligibility({});
            setSelectedHouseholdClientIds([]);
            setLoadingHouseholdEligibility(false);
            return;
        }

        const targets = buildHouseholdTrackingTargets(selectedClient, householdTrackingGroups);
        setHouseholdEligibility(targets.reduce<Record<string, HouseholdTrackingEligibility>>((result, target) => {
            result[target.clientId] = target.clientId === selectedClient.id
                ? { status: 'eligible' }
                : { status: 'checking' };
            return result;
        }, {}));
        setLoadingHouseholdEligibility(true);

        const eligibility = await resolveHouseholdTrackingEligibility(
            targets,
            selectedClient.id,
            selectedProgram,
            getEnrolledPrograms
        );

        if (householdEligibilityRequest.current !== requestId) {
            return;
        }

        setHouseholdEligibility(eligibility);
        setSelectedHouseholdClientIds(getSelectableHouseholdTrackingClientIds(
            targets,
            eligibility
        ));
        setLoadingHouseholdEligibility(false);
    };

    const handleSubmitForm = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrors([]);
        setSubmitError(undefined);

        if (!selectedClient || !selectedClient.id) {
            setErrors(['You must select a participant first before submission']);
            // we display a warning for not selecting
            return;
        }

        if (selectedForm && selectedForm.id) {
            setSubmitting(true);

            const createRequest = (clientId: string) => createFormInstance({
                formId: selectedForm.id,
                versionId: selectedForm.versionId,
                clientId,
                fields: fields.map(f => {
                    return {
                        fieldId: f.id,
                        value: normalizeFormInstanceFieldValue(f, f.value),
                        files: f.files?.map(f => f.fileId!) || []
                    }
                })
            });

            if (!submitForHousehold) {
                try {
                    await createRequest(selectedClient.id);
                    setSelectedClient(undefined);
                    resetHouseholdTracking();
                    handleFormReset();
                    loadInstances(typeId);
                } catch (error) {
                    setSubmitError(error);
                    console.error(error);
                } finally {
                    setSubmitting(false);
                    organizationContext.refresh();
                }
                return;
            }

            const selectedClientIds = new Set(selectedHouseholdClientIds);
            const eligibleTargets = buildHouseholdTrackingTargets(selectedClient, householdTrackingGroups)
                .filter(target =>
                    householdEligibility[target.clientId]?.status === 'eligible'
                    && selectedClientIds.has(target.clientId)
                );
            if (eligibleTargets.length === 0) {
                setErrors(['Select at least one eligible participant for household tracking submission']);
                setSubmitting(false);
                return;
            }

            let completed = 0;
            setHouseholdSummary(undefined);
            setHouseholdProgress({
                completed: 0,
                total: eligibleTargets.length
            });

            const results = await runHouseholdTrackingSubmissions(
                eligibleTargets,
                target => createRequest(target.clientId),
                {
                    concurrency: 3,
                    retries: 3,
                    baseDelayMs: 1000,
                    onJobUpdate: (target, status, attempt) => {
                        setHouseholdProgress(current => ({
                            completed: current?.completed || completed,
                            total: eligibleTargets.length,
                            activeName: target.name,
                            activeAttempt: attempt,
                            activeStatus: status
                        }));
                    },
                    onJobComplete: () => {
                        completed += 1;
                        setHouseholdProgress(current => ({
                            completed,
                            total: eligibleTargets.length,
                            activeName: current?.activeName,
                            activeAttempt: current?.activeAttempt,
                            activeStatus: current?.activeStatus
                        }));
                    }
                }
            );

            const succeeded = results.filter(result => result.status === 'succeeded').length;
            const failed = results.length - succeeded;
            setHouseholdProgress(undefined);
            setHouseholdSummary({
                totalPeopleReviewed: getHouseholdTrackingReviewedPeopleCount(selectedClient, householdTrackingGroups),
                eligibleParticipants: eligibleTargets.length,
                succeeded,
                failed,
                ineligible: getHouseholdTrackingIneligiblePeopleCount(householdTrackingGroups, householdEligibility),
                results
            });
            setSubmitting(false);
            handleFormReset();
            loadInstances(typeId);
            organizationContext.refresh();

            if (failed === 0) {
                toast.success(`Successfully submitted form for ${succeeded} participant${succeeded === 1 ? '' : 's'}`);
                setSelectedClient(undefined);
                resetHouseholdTracking();
            } else {
                toast.error(`Submission completed with ${failed} failure${failed === 1 ? '' : 's'}`);
            }
        } else {
            setErrors(['No selected form']);
        }
    };

    const handleOnClosePreview = () => {
        setHighlightInstance(previewInstance);
        setPreviewInstance(undefined);
    };

    const handleFormReset = () => {
        setFields([
            ...fields.map(f => {
                return {
                    ...f,
                    value: normalizeFormInstanceFieldValue(f),
                    files: []
                }
            })
        ])
    };
    
    const handleConfirmDeletion = (instance: LatestFormInstance) => {
        setConfirmInstanceDeletion(instance);
        setModalErrors(undefined);
        setDeleting(false);
    };
    
    const handlePreviewInstance = (instance: LatestFormInstance) => {
        setPreviewInstance(instance);
        setModalErrors(undefined);
    };

    const handleDeletion = () => {
        if (!confirmInstanceDeletion) {
            return;
        }

        setDeleting(true);

        deleteFormInstance(confirmInstanceDeletion.formId, confirmInstanceDeletion.id).then(() => {
            setConfirmInstanceDeletion(undefined);            
            loadInstances(typeId);
        }).catch(e => {
            console.error(e);
            setModalErrors(e);
        }).finally(() => {
            setDeleting(false);
        });
    };

    const handleOnPrint = () => {
        window.print();
    }

    const getParticipantName = () => {
        const firstName = selectedClient?.firstName || '';
        const lastName = selectedClient?.lastName || '';

        return `${firstName} ${lastName}`.trim();
    }

    const getMainContent = () => {
        return (<>
            {selectedForm && fields && fields.length > 0 && <div className="row margin-top-3">
                <div className="column">
                    <form onSubmit={handleSubmitForm}>
                        <ErrorMessages messages={errors} />
                        <ApiErrorMessages error={submitError} />
                        <div className="row justify-content-end hide-on-print">
                            <div className="column-auto">
                                <Button id="print-button" 
                                    className="secondary"
                                    text="Print" 
                                    disabled={submitting}
                                    onClick={handleOnPrint} />
                            </div>
                        </div>
                        <div className="column">
                            <div className="show-on-print">
                                <Input id="client-name"
                                    label="Participant Name"
                                    inputClassName="margin-bottom-5"
                                    required={true} 
                                    value={getParticipantName()} />
                                <hr/>
                            </div>
                            {bulkHouseholdTrackingEnabled && selectedClient && selectedProgram && <div className="hide-on-print">
                                <HouseholdTrackingControls
                                    key={`${selectedClient.id}-${selectedProgram.id}-${selectedForm.id}`}
                                    groups={householdTrackingGroups}
                                    eligibility={householdEligibility}
                                    loadingEligibility={loadingHouseholdEligibility}
                                    programName={selectedProgram.name}
                                    selectedClientIds={selectedHouseholdClientIds}
                                    checked={submitForHousehold}
                                    disabled={submitting || loadingSelectedClient}
                                    onChange={handleSubmitForHouseholdChange}
                                    onSelectedClientIdsChange={setSelectedHouseholdClientIds} />
                            </div>}
                            <FormEditor disabled={submitting} fields={fields} onChange={handleOnFieldsChange} />
                            {householdProgress && <HouseholdTrackingProgress progress={householdProgress} />}
                        </div>
                        <div className="row margin-top-3 justify-content-between hide-on-print">
                            <div className="column-auto">
                                <Button id="reset-button" 
                                    className="secondary" 
                                    text="Reset" 
                                    disabled={submitting || loadingHouseholdEligibility || !selectedClient}
                                    onClick={handleFormReset} />
                            </div>
                            <div className="column-auto">
                                <Button id="submit-button" 
                                    text={submitForHousehold ? "Submit for Selected Participants" : "Submit"}
                                    disabled={submitting || loadingSelectedClient || loadingHouseholdEligibility || !selectedClient || fields.some(f => f.files && f.files.some(ff => ff.status !== UPLOADED))}
                                    loading={submitting} 
                                    type="submit" />
                            </div>
                        </div>
                    </form>
                    {householdSummary && <HouseholdTrackingSummary
                        summary={householdSummary}
                        groups={householdTrackingGroups}
                        eligibility={householdEligibility} />}
                </div>                
            </div>}
            {loadingFields && <Loader />}
            <div className="hide-on-print">
            <h3>Recent Submissions</h3>
            {loadingInstances && <Loader />}
            {!loadingInstances && <div className="list-item-container">
                {instances && instances.length > 0 && instances.map(i => {
                    const options: { label: string, href?: string, onClick?: () => void, newWindow?: boolean; }[] = [
                        {
                            label: 'View',
                            href: `/forms/${i.formId}/versions/${i.formVersionId}/instances/${i.id}/view`
                        }                        
                    ];

                    options.push({
                        label: 'Preview',
                        onClick: () => handlePreviewInstance(i)
                    });
                    
                    if (me.user?.userRoleId === ADMIN_ROLE_ID || me.user?.userRoleId === CONTRIBUTOR_ROLE_ID) {
                        options.push({
                            label: 'Edit',
                            href: `/forms/${i.formId}/versions/${i.formVersionId}/instances/${i.id}/edit`
                        });
                        
                        options.push({
                            label: 'Delete',
                            onClick: () => handleConfirmDeletion(i)
                        });
                    }

                    return <div className={getclassNames('list-item', highlightInstance === i ? 'highlight' : '')} key={i.id}>
                        <div className="row justify-content-between align-items-center">
                            <div className="column-4">
                                {i.name}
                            </div>
                            <div className="column-6">
                                <div className="row">
                                    <div className="column">
                                        {i.clientFirstName} {i.clientLastName}
                                    </div>
                                    <div className="column">
                                        {formatDistance(parseISO(i.updated || i.created), new Date(), { addSuffix: true })}
                                    </div>
                                </div>
                            </div>
                            <div className="column">
                                <div className="row justify-content-end">
                                    <div className="column-auto">
                                        <Dropdown id={`dropdown-instances-${i.id}`} 
                                            options={options} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                })}
                {instances && instances.length === 0 && <div>No recent submissions</div>}
            </div>}
            <Modal id="preview-modal" visible={previewInstance !== undefined} className="medium" title="Preview Form Submission" onClose={handleOnClosePreview}>
                {previewInstance && <>
                    <PreviewFormInstance formId={previewInstance.formId} 
                        instanceId={previewInstance.id}
                        versionId={previewInstance.formVersionId} />                    
                    <div className="row margin-top-5">
                        <div className="column">
                            <div className="row justify-content-end">
                                <div className="column-auto">
                                    <Button id="cancel-delete"
                                        text="Close" 
                                        className="secondary" 
                                        onClick={handleOnClosePreview} />
                                </div>
                            </div>
                        </div>
                    </div>
                </>}
            </Modal>
            <Modal id="delete-modal" visible={confirmInstanceDeletion !== undefined} className="small" title="Confirm Delete Form Submission" onClose={() => setConfirmInstanceDeletion(undefined)}>
                    {confirmInstanceDeletion && <>
                        <ApiErrorMessages error={modalErrors} />
                        <div className="row">
                            <div className="column">
                                <div>Are you sure you want to delete this Form Submission?</div>
                                <br/>
                                <div>This action is irreversible.</div>
                                <br/>
                                <div>Form Name: {confirmInstanceDeletion.name}</div>
                                <div>Participant: {confirmInstanceDeletion.clientFirstName} {confirmInstanceDeletion.clientLastName}</div>
                                <div>{formatDistance(parseISO(confirmInstanceDeletion.updated || confirmInstanceDeletion.created), new Date(), { addSuffix: true })}</div>
                            </div>
                        </div>
                        <div className="row margin-top-5">
                            <div className="column">
                                <div className="row justify-content-between">
                                    <div className="column-auto">
                                        <Button id="cancel-delete"
                                            text="Cancel" 
                                            className="secondary" 
                                            disabled={deleting} 
                                            onClick={() => setConfirmInstanceDeletion(undefined)} />
                                    </div>
                                    <div className="column-auto">
                                        <Button id="execute-delete"
                                            text="Delete"
                                            className="warning"
                                            disabled={deleting}
                                            loading={deleting}
                                            onClick={handleDeletion} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>}
                </Modal>
            </div>
        </>
        );
    };

    const resetSelections = () => {
        resetHouseholdTracking();
        setSelectedClient(undefined);
        setSelectedProgram(undefined);
        setSelectedForm(undefined);
        setFields([]);
    };

    return (
        <section id="main-content" tabIndex={-1}>
            <div className="section">
                <SideMenu ariaLabel="Track Data Side Menu"
                    id="tracking-menu"
                    title="Track Data"
                    options={getTrackingOptions(resetSelections)}
                    headerContent={getHeaderContent()}
                    mainContent={getMainContent()}
                />
            </div>

        </section>)
        ;
}
